/**
 * Indeel-popover (Epic C-3 — Planbord & indelen, Story C-3.3, UX-C1)
 *
 * Custom, lokaal gepositioneerde anchored overlay (géén Radix Popover/
 * Command — dit project heeft geen `@radix-ui/*`/`cmdk`-dependency, AD-18) —
 * zelfde click-buiten/Escape-to-close-interactieprincipe als de bestaande
 * modals (`UitDienstDialog.tsx`), hier alleen anchored i.p.v. gecentreerd.
 * De "zoekbare takenlijst" is een simpel `<input>`-zoekveld + gefilterde
 * `<ul>`-lijst; tooltips via het native `title`-attribuut.
 *
 * Bewuste afwijking van de story-tekst ("sonner"): dit project's
 * `features/planning`-module gebruikt overal al de bestaande app-brede
 * `useToast()` (zie `TakenBeheerPage.tsx`/`QualificationCheckbox.tsx`) —
 * geen tweede toast-systeem introduceren voor één scherm (AD-18-geest).
 * De fout-toast zelf leeft in `PlanningCell.tsx` (die de mutatie-uitkomst
 * kent), niet hier.
 */

import { useEffect, useRef, useState } from 'react';
import type { BoardAssignmentEntry, PlanningTaskDTO } from '@functieloonhuis/shared';

interface AssignPopoverProps {
  employeeFirstName: string;
  dayLabel: string;
  dagdeelLabel: string;
  /** Alleen aangeboden op een merged cel zonder bestaande indeling (AC 2). */
  canHeleDag: boolean;
  currentAssignment: BoardAssignmentEntry | null;
  /**
   * Story C-4.3 (AC 3) — de C-4.2-oorzaaktekst wanneer deze popover vanaf
   * een conflict-cel is geopend ("conflict-modus"): toont een rode notice
   * boven de gewone inhoud, naast de al bestaande "Indeling verwijderen"-
   * actie hieronder (`currentAssignment` is altijd gezet voor een conflict).
   */
  conflictNotice?: string | null;
  tasks: PlanningTaskDTO[];
  unqualifiedTaskIds: number[];
  canManageTasks: boolean;
  onClose: () => void;
  onPick: (task: PlanningTaskDTO, heleDag: boolean) => void;
  onRemove: () => void;
}

export function AssignPopover({
  employeeFirstName,
  dayLabel,
  dagdeelLabel,
  canHeleDag,
  currentAssignment,
  conflictNotice,
  tasks,
  unqualifiedTaskIds,
  canManageTasks,
  onClose,
  onPick,
  onRemove,
}: AssignPopoverProps) {
  const [search, setSearch] = useState('');
  const [heleDag, setHeleDag] = useState(false);
  const [pendingTask, setPendingTask] = useState<PlanningTaskDTO | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus het zoekveld zodra de popover opent (niet via het `autoFocus`-
  // attribuut — `jsx-a11y/no-autofocus` — maar via een expliciete effect,
  // zelfde uitkomst zonder de lint-regel te schenden).
  useEffect(() => {
    if (!pendingTask) searchInputRef.current?.focus();
  }, [pendingTask]);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose();
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const activeTasks = tasks.filter((t) => !t.isArchived);
  const filteredTasks = search.trim()
    ? activeTasks.filter((t) => t.name.toLowerCase().includes(search.trim().toLowerCase()))
    : activeTasks;

  function handleTaskClick(task: PlanningTaskDTO) {
    if (unqualifiedTaskIds.includes(task.id)) {
      setPendingTask(task);
      return;
    }
    onPick(task, heleDag && canHeleDag);
  }

  const title = pendingTask
    ? null
    : `Deel ${employeeFirstName} in — ${dayLabel} ${dagdeelLabel}`.trim();

  return (
    <div
      ref={rootRef}
      data-testid="assign-popover"
      role="dialog"
      aria-label={title ?? `Kwalificatie-waarschuwing — ${employeeFirstName}`}
      className="absolute z-30 top-full left-0 mt-1 w-64 rounded-md border border-[#E2E8F0] bg-white shadow-lg p-3"
    >
      {pendingTask ? (
        <div data-testid="kwalificatie-waarschuwing-blok" className="text-sm">
          <div className="flex gap-2 mb-3 rounded-md bg-[#FFFBEB] border border-[#FDE68A] px-3 py-2 text-[#B45309]">
            <span aria-hidden="true">&#9650;</span>
            <p>
              Let op: {employeeFirstName} mist de kwalificatie
              {pendingTask.requiredActivity ? ` ${pendingTask.requiredActivity}` : ''}
              {pendingTask.requiredProductCategoryId !== null ? ` · categorie ${pendingTask.requiredProductCategoryId}` : ''}
              {pendingTask.requiredTargetMarketId !== null ? ` · markt ${pendingTask.requiredTargetMarketId}` : ''}.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setPendingTask(null)}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 rounded-md border border-[#E2E8F0] hover:bg-gray-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={() => onPick(pendingTask, heleDag && canHeleDag)}
              className="px-3 py-1.5 text-xs font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90"
            >
              Toch indelen
            </button>
          </div>
        </div>
      ) : (
        <>
          <h3 className="text-sm font-medium text-[#1E293B] mb-2">{title}</h3>

          {conflictNotice && (
            <div data-testid="conflict-notice" className="flex gap-2 mb-2 rounded-md bg-[#D64545]/10 border border-[#D64545]/30 px-3 py-2 text-xs" style={{ color: '#D64545' }}>
              <span aria-hidden="true">&#9888;</span>
              <p>Conflict — {conflictNotice}</p>
            </div>
          )}

          {currentAssignment && (
            <div className="mb-2 flex items-center justify-between rounded-md bg-gray-50 border border-[#E2E8F0] px-2 py-1.5 text-xs">
              <span>
                Huidige taak: <strong>{currentAssignment.taskName}</strong>
              </span>
              <button type="button" onClick={onRemove} className="text-gray-600 hover:underline font-medium">
                Indeling verwijderen
              </button>
            </div>
          )}

          <input
            ref={searchInputRef}
            type="text"
            data-testid="assign-popover-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek een taak…"
            className="w-full mb-2 px-2 py-1.5 text-sm border border-[#E2E8F0] rounded-md focus:outline-none focus:ring-2 focus:ring-[#54949E]"
          />

          {canHeleDag && (
            <label className="flex items-center gap-2 mb-2 text-xs text-gray-700">
              <input
                type="checkbox"
                data-testid="assign-popover-hele-dag"
                checked={heleDag}
                onChange={(e) => setHeleDag(e.target.checked)}
                className="rounded border-[#E2E8F0] text-[#54949E] focus:ring-[#54949E]"
              />
              Hele dag (ochtend + middag)
            </label>
          )}

          {activeTasks.length === 0 ? (
            <div data-testid="assign-popover-geen-taken" className="text-xs text-gray-500 py-3 text-center">
              <p>Nog geen taken — maak eerst een taak aan</p>
              {canManageTasks && (
                <a href="/planning/taken" className="text-[#54949E] hover:underline">
                  Naar Taken
                </a>
              )}
            </div>
          ) : (
            <ul className="max-h-40 overflow-y-auto" role="listbox" aria-label="Takenlijst">
              {filteredTasks.map((task) => (
                <li key={task.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => handleTaskClick(task)}
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-sm text-left rounded hover:bg-gray-50"
                  >
                    <span aria-hidden="true" className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ backgroundColor: task.color ?? '#E2E8F0' }} />
                    {task.name}
                    {unqualifiedTaskIds.includes(task.id) && (
                      <span aria-hidden="true" className="ml-auto text-[10px] font-bold" style={{ color: '#B45309' }}>
                        &#9650;
                      </span>
                    )}
                  </button>
                </li>
              ))}
              {filteredTasks.length === 0 && <li className="text-xs text-gray-500 px-2 py-2">Geen taken gevonden.</li>}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
